import { forwardRef, memo, useLayoutEffect, useRef, useState } from 'react';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import { useBaselineSnap } from '../shared/formBaselines';
import { buildUdyogForm } from './udyogFormEngine';
import { PAGE_GEOMETRY } from './udyogGeometry';
import {
    BASELINE_RATIO,
    BODY_SIZE,
    COLOR,
    LINE_BOX,
    PAGES,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    VALUE_INK,
    VALUE_TYPE,
} from './udyogMetrics';

export const UDYOG_PAGE_WIDTH = PAGE_WIDTH;
export const UDYOG_PAGE_HEIGHT = PAGE_HEIGHT;
export const UDYOG_PAGE_COUNT = PAGES.length;

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: COLOR.ink,
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: BODY_SIZE,
    overflow: 'hidden',
};

/**
 * The form's headings are Kalpurush drawn with a fill and a stroke. Kalpurush has
 * one weight, and html2canvas does not reproduce the browser's synthetic bold,
 * but it does paint text-shadow — so a hairline shadow gives both the same ink.
 */
const boldShadow = (color) => `0.22px 0 0 ${color}, -0.22px 0 0 ${color}, 0 0.1px 0 ${color}`;

const lineStyle = (size, baseline) => ({
    position: 'absolute',
    top: baseline - size * BASELINE_RATIO,
    fontSize: size,
    fontWeight: 400,
    lineHeight: LINE_BOX,
    fontFamily: 'inherit',
});

let measureCanvas = null;
function measureWidth(text, size) {
    if (typeof document === 'undefined') return text.length * size * 0.5;
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    ctx.font = `400 ${size}px ${FAMILY_CARD_FONT_FAMILY}`;
    return ctx.measureText(text).width;
}

/** One printed line, on its measured baseline. `right` justifies it to that edge. */
function Text({ item }) {
    const ref = useRef(null);
    const size = item.size ?? BODY_SIZE;
    const color = item.color ?? COLOR.ink;
    useBaselineSnap(ref, [item.t, size]);

    // The declaration's full lines run margin to margin; Word does that by
    // widening the spaces, so the same is done here once Kalpurush has loaded.
    useLayoutEffect(() => {
        if (item.right == null) return undefined;
        const justify = () => {
            const spaces = item.t.split(' ').length - 1;
            const slack = item.right - item.x - measureWidth(item.t, size);
            if (ref.current && spaces) ref.current.style.wordSpacing = `${slack / spaces}px`;
        };
        justify();
        let cancelled = false;
        document.fonts?.load(`${size}px Kalpurush`).then(() => {
            if (!cancelled) justify();
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [item, size]);

    return (
        <span
            ref={ref}
            data-baseline={item.y}
            style={{
                ...lineStyle(size, item.y),
                left: item.x,
                whiteSpace: 'pre',
                color,
                textShadow: item.bold ? boldShadow(color) : undefined,
            }}
        >
            {item.t}
        </span>
    );
}

/** One line of an answer. */
function AnswerLine({ text, size, baseline, left, width, align }) {
    const ref = useRef(null);
    useBaselineSnap(ref, [text, size, baseline]);
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            data-baseline={baseline}
            style={{
                ...lineStyle(size, baseline),
                left,
                width,
                textAlign: align,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textShadow: VALUE_INK_SHADOW,
            }}
        >
            {text}
        </span>
    );
}

/** Break at spaces and at the user's own line breaks, as many lines as it takes. */
function breakLines(text, width, size) {
    const lines = [];
    for (const paragraph of text.split('\n')) {
        const words = paragraph.split(/\s+/).filter(Boolean);
        let line = '';
        for (const word of words) {
            const next = line ? `${line} ${word}` : word;
            if (line && measureWidth(next, size) > width) {
                lines.push(line);
                line = word;
            } else {
                line = next;
            }
        }
        if (line) lines.push(line);
    }
    return lines;
}

/**
 * The largest size from `size` down at which the answer fits its cell, wrapped.
 * Whatever still does not fit at the smallest size is cut at the cell's last line.
 */
function fitToCell(text, item) {
    const max = item.size ?? VALUE_TYPE.size;
    const min = Math.min(max, item.minSize ?? VALUE_TYPE.minSize);
    const room = item.h - 2;
    for (let size = max; ; size = Math.max(min, size - 0.25)) {
        const lines = breakLines(text, item.w, size);
        const tall = (lines.length - 1) * size * VALUE_TYPE.lineHeight + size * (VALUE_INK.ascent + VALUE_INK.descent);
        const wide = lines.every((line) => measureWidth(line, size) <= item.w);
        if ((tall <= room && wide) || size <= min) {
            const most = Math.max(1, Math.floor((room - size * (VALUE_INK.ascent + VALUE_INK.descent)) / (size * VALUE_TYPE.lineHeight)) + 1);
            return { lines: lines.slice(0, most), size };
        }
    }
}

/**
 * An answer in a cell, from a passport number to a 200-word business idea. Each
 * line is placed on its own baseline rather than left to wrap, so the block sits
 * centred between the cell's rules in the preview and the export alike.
 */
function Area({ item, text }) {
    const [fit, setFit] = useState(() => (text ? fitToCell(text, item) : null));

    useLayoutEffect(() => {
        if (!text) { setFit(null); return undefined; }
        setFit(fitToCell(text, item));
        let cancelled = false;
        document.fonts?.load(`${item.size ?? VALUE_TYPE.size}px Kalpurush`).then(() => {
            if (!cancelled) setFit(fitToCell(text, item));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [text, item]);

    if (!text || !fit) return null;

    const pitch = fit.size * VALUE_TYPE.lineHeight;
    const inkCentre = ((VALUE_INK.ascent - VALUE_INK.descent) / 2) * fit.size;
    const first = item.y + item.h / 2 - ((fit.lines.length - 1) * pitch) / 2 + inkCentre;

    return fit.lines.map((line, n) => (
        <AnswerLine
            // eslint-disable-next-line react/no-array-index-key
            key={n}
            text={line}
            size={fit.size}
            baseline={first + n * pitch}
            left={item.x}
            width={item.w}
            align={item.align}
        />
    ));
}

/** An answer written over a run of printed dots: a count, or the signing date. */
function Dots({ item, text }) {
    const ref = useRef(null);
    const size = VALUE_TYPE.size;
    const [fitRef, fitted] = useFitFontSize(size, 7, [text, item.w]);
    useBaselineSnap(ref, [text, fitted]);
    if (!text) return null;
    const baseline = item.y - 1.6;
    return (
        <span
            ref={(node) => { ref.current = node; fitRef.current = node; }}
            data-field-value
            data-filled="true"
            data-baseline={baseline}
            style={{
                ...lineStyle(fitted, baseline),
                left: item.x,
                width: item.w,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textShadow: VALUE_INK_SHADOW,
            }}
        >
            {text}
        </span>
    );
}

function Item({ item, d }) {
    switch (item.k) {
        case 'area': return <Area item={item} text={d[item.field]} />;
        case 'dots': return <Dots item={item} text={d[item.field]} />;
        default: return null;
    }
}

const FILL = { g: COLOR.shade, d: COLOR.heading };

/**
 * Everything on a page that never changes: the rules, the shading, the printed
 * text. Kept out of the typing path — it is a few hundred spans a page.
 */
const PrintedPage = memo(({ index }) => {
    const { rules, fills } = PAGE_GEOMETRY[index];
    return (
        <>
            {fills.map(([x, y, w, h, tone], i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`f${i}`} aria-hidden style={{ position: 'absolute', left: x, top: y, width: w, height: h, background: FILL[tone] }} />
            ))}
            {rules.map(([x, y, w, h], i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`r${i}`} aria-hidden style={{ position: 'absolute', left: x, top: y, width: w, height: h, background: '#000' }} />
            ))}
            {PAGES[index].map((item, i) => {
                const key = `${item.k}-${i}`;
                if (item.k === 'text') return <Text key={key} item={item} />;
                if (item.k === 'logo') {
                    return (
                        <img
                            key={key}
                            src="/images/udyog-logo.png"
                            alt=""
                            aria-hidden
                            style={{ position: 'absolute', left: item.x, top: item.y, width: item.w, height: item.h }}
                        />
                    );
                }
                if (item.k === 'bullet') {
                    return (
                        <span
                            key={key}
                            aria-hidden
                            style={{ position: 'absolute', left: item.x, top: item.y, width: 3.2, height: 3.2, borderRadius: '50%', background: '#000' }}
                        />
                    );
                }
                return null;
            })}
        </>
    );
});

function Page({ index, d }) {
    return (
        <div data-udyog-page style={PAGE_STYLE}>
            <PrintedPage index={index} />
            {PAGES[index].map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Item key={`${item.k}-${i}`} item={item} d={d} />
            ))}
        </div>
    );
}

function UdyogFormPreview({ form }, ref) {
    const d = buildUdyogForm(form);

    return (
        <div ref={ref} data-udyog-document style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {PAGES.map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Page key={index} index={index} d={d} />
            ))}
        </div>
    );
}

export default forwardRef(UdyogFormPreview);
